import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Filter, Settings as SettingsIcon, Calendar as CalendarIcon, X } from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import esLocale from '@fullcalendar/core/locales/es';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ClientSearchSelect } from '@/components/ClientSearchSelect';
import { TicketSearchSelect } from '@/components/TicketSearchSelect';
import { confirmAsync } from '@/lib/confirm';

export default function Teamup({ user }) {
  const calendarRef = useRef(null);
  
  // State
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(['planned', 'in_progress']);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  
  // Event Dialog
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    category_id: '',
    assigned_to_user_id: '',
    customer_id: '',
    ticket_id: '',
    status: 'planned'
  });

  useEffect(() => {
    fetchCategories();
    fetchEvents();
    fetchUsers();
    fetchCustomers();
  }, []);

  useEffect(() => {
    // Auto-select all categories when loaded
    if (categories.length > 0 && selectedCategories.length === 0) {
      setSelectedCategories(categories.map(c => c.id));
    }
  }, [categories]);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/teamup/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Error al cargar categorías');
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axiosInstance.get('/teamup/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axiosInstance.get('/customers');
      // El endpoint ahora devuelve paginación: {items: [], total: int}
      setCustomers(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      setCustomers([]);
    }
  };

  // Filter events based on selected categories and statuses
  const filteredEvents = events.filter(event => {
    const categoryMatch = selectedCategories.length === 0 || selectedCategories.includes(event.category_id);
    const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(event.status);
    return categoryMatch && statusMatch;
  });

  // Transform events for FullCalendar
  const calendarEvents = filteredEvents.map(event => ({
    id: event.id,
    title: event.title,
    start: event.start_date,
    end: event.end_date,
    backgroundColor: event.category_color,
    borderColor: event.category_color,
    extendedProps: {
      ...event,
      // Apply opacity for completed/cancelled events
      opacity: ['completed', 'cancelled'].includes(event.status) ? 0.5 : 1
    }
  }));

  const handleDateClick = (info) => {
    // Open dialog to create new event
    const startDate = new Date(info.date);
    const endDate = new Date(info.date);
    endDate.setHours(endDate.getHours() + 1);
    
    setEventForm({
      title: '',
      description: '',
      start_date: format(startDate, "yyyy-MM-dd'T'HH:mm"),
      end_date: format(endDate, "yyyy-MM-dd'T'HH:mm"),
      category_id: categories[0]?.id || '',
      assigned_to_user_id: user?.id || '',
      customer_id: '',
      ticket_id: '',
      status: 'planned'
    });
    setEditingEvent(null);
    setEventDialogOpen(true);
  };

  const handleEventClick = (info) => {
    // Open dialog to edit event
    const event = info.event.extendedProps;
    setEventForm({
      title: event.title,
      description: event.description || '',
      start_date: format(new Date(event.start_date), "yyyy-MM-dd'T'HH:mm"),
      end_date: format(new Date(event.end_date), "yyyy-MM-dd'T'HH:mm"),
      category_id: event.category_id,
      assigned_to_user_id: event.assigned_to_user_id || '',
      customer_id: event.customer_id || '',
      ticket_id: event.ticket_id || '',
      status: event.status
    });
    setEditingEvent(event);
    setEventDialogOpen(true);
  };

  const handleEventDrop = async (info) => {
    // Handle drag and drop event
    const event = info.event;
    const eventId = event.extendedProps.id;
    
    try {
      // Calculate new dates
      const startDate = new Date(event.start);
      const endDate = new Date(event.end);
      
      // Update event on backend
      await axiosInstance.patch(`/teamup/events/${eventId}/dates`, {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      });
      
      toast.success('Evento movido correctamente');
      fetchEvents(); // Refresh to ensure consistency
    } catch (error) {
      // Revert the change if it fails
      info.revert();
      toast.error(error.response?.data?.detail || 'Error al mover el evento');
    }
  };

  const handleEventResize = async (info) => {
    // Handle event resize (duration change)
    const event = info.event;
    const eventId = event.extendedProps.id;
    
    try {
      // Calculate new dates
      const startDate = new Date(event.start);
      const endDate = new Date(event.end);
      
      // Validate that end is after start
      if (endDate <= startDate) {
        info.revert();
        toast.error('La fecha de fin debe ser posterior a la fecha de inicio');
        return;
      }
      
      // Update event on backend
      await axiosInstance.patch(`/teamup/events/${eventId}/dates`, {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      });
      
      toast.success('Duración del evento actualizada');
      fetchEvents(); // Refresh to ensure consistency
    } catch (error) {
      // Revert the change if it fails
      info.revert();
      toast.error(error.response?.data?.detail || 'Error al cambiar la duración');
    }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    
    // Validate dates
    if (new Date(eventForm.end_date) <= new Date(eventForm.start_date)) {
      toast.error('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }

    try {
      const eventData = {
        ...eventForm,
        start_date: new Date(eventForm.start_date).toISOString(),
        end_date: new Date(eventForm.end_date).toISOString(),
        assigned_to_user_id: eventForm.assigned_to_user_id || null,
        customer_id: eventForm.customer_id || null,
        ticket_id: eventForm.ticket_id || null
      };

      if (editingEvent) {
        await axiosInstance.put(`/teamup/events/${editingEvent.id}`, eventData);
        toast.success('Evento actualizado correctamente');
      } else {
        await axiosInstance.post('/teamup/events', eventData);
        toast.success('Evento creado correctamente');
      }

      setEventDialogOpen(false);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar evento');
    }
  };

  const handleDeleteEvent = async () => {
    if (!editingEvent) return;

    if (await confirmAsync('¿Estás seguro de que quieres eliminar este evento?')) {
      try {
        await axiosInstance.delete(`/teamup/events/${editingEvent.id}`);
        toast.success('Evento eliminado correctamente');
        setEventDialogOpen(false);
        fetchEvents();
      } catch (error) {
        toast.error('Error al eliminar evento');
      }
    }
  };

  const toggleCategory = (categoryId) => {
    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(selectedCategories.filter(id => id !== categoryId));
    } else {
      setSelectedCategories([...selectedCategories, categoryId]);
    }
  };

  const toggleStatus = (status) => {
    if (selectedStatuses.includes(status)) {
      setSelectedStatuses(selectedStatuses.filter(s => s !== status));
    } else {
      setSelectedStatuses([...selectedStatuses, status]);
    }
  };

  const toggleAllCategories = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(categories.map(c => c.id));
    }
  };

  const statusConfig = {
    planned: { label: 'Planificado', color: 'bg-blue-100 text-blue-800' },
    in_progress: { label: 'En Curso', color: 'bg-yellow-100 text-yellow-800' },
    completed: { label: 'Completado', color: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Cancelado', color: 'bg-gray-100 text-gray-800' }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-500">Cargando calendario...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#212B35]">Teamup</h1>
            <p className="text-gray-600">Calendario de equipo - Planifica intervenciones, guardias y citas</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setFilterPanelOpen(!filterPanelOpen)}
              variant="outline"
              className="border-[#9C0F17] text-[#9C0F17] hover:bg-[#9C0F17] hover:text-white"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtros
              {(selectedCategories.length < categories.length || selectedStatuses.length < 4) && (
                <Badge className="ml-2 bg-[#9C0F17] text-white">
                  {(categories.length - selectedCategories.length) + (4 - selectedStatuses.length)}
                </Badge>
              )}
            </Button>
            <Button
              onClick={() => {
                setEventForm({
                  title: '',
                  description: '',
                  start_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
                  end_date: format(new Date(Date.now() + 3600000), "yyyy-MM-dd'T'HH:mm"),
                  category_id: categories[0]?.id || '',
                  assigned_to_user_id: user?.id || '',
                  customer_id: '',
                  ticket_id: '',
                  status: 'planned'
                });
                setEditingEvent(null);
                setEventDialogOpen(true);
              }}
              className="bg-[#9C0F17] hover:bg-[#7A0C12]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Evento
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Filter Panel */}
          {filterPanelOpen && (
            <Card className="col-span-12 md:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Filtros
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilterPanelOpen(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Category Filters */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-semibold">Categorías</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleAllCategories}
                      className="text-xs"
                    >
                      {selectedCategories.length === categories.length ? 'Ninguno' : 'Todos'}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {categories.map(category => (
                      <label
                        key={category.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category.id)}
                          onChange={() => toggleCategory(category.id)}
                          className="rounded"
                        />
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="text-sm">{category.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Status Filters */}
                <div>
                  <Label className="font-semibold mb-2 block">Estados</Label>
                  <div className="space-y-2">
                    {Object.entries(statusConfig).map(([status, config]) => (
                      <label
                        key={status}
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={selectedStatuses.includes(status)}
                          onChange={() => toggleStatus(status)}
                          className="rounded"
                        />
                        <Badge className={config.color}>{config.label}</Badge>
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Calendar */}
          <Card className={filterPanelOpen ? 'col-span-12 md:col-span-9' : 'col-span-12'}>
            <CardContent className="pt-6">
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                initialView="dayGridMonth"
                locale={esLocale}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                buttonText={{
                  today: 'Hoy',
                  month: 'Mes',
                  week: 'Semana',
                  day: 'Día'
                }}
                events={calendarEvents}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                editable={true}
                droppable={true}
                eventDrop={handleEventDrop}
                eventResize={handleEventResize}
                height="auto"
                eventDisplay="block"
                eventTimeFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                }}
                slotLabelFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                }}
                eventDidMount={(info) => {
                  // Apply opacity to completed/cancelled events
                  if (info.event.extendedProps.opacity) {
                    info.el.style.opacity = info.event.extendedProps.opacity;
                  }
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* Event Dialog */}
        <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingEvent ? 'Editar Evento' : 'Nuevo Evento'}
              </DialogTitle>
              <DialogDescription>
                {editingEvent ? 'Modifica los detalles del evento' : 'Crea un nuevo evento en el calendario'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveEvent} className="space-y-4">
              <div>
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  placeholder="Ej: Instalación TPV Bar Central"
                  required
                />
              </div>

              <div>
                <Label htmlFor="category">Categoría *</Label>
                <Select
                  value={eventForm.category_id}
                  onValueChange={(value) => setEventForm({ ...eventForm, category_id: value })}
                >
                  <SelectTrigger id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded"
                            style={{ backgroundColor: cat.color }}
                          />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Fecha/Hora Inicio *</Label>
                  <Input
                    id="start_date"
                    type="datetime-local"
                    value={eventForm.start_date}
                    onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">Fecha/Hora Fin *</Label>
                  <Input
                    id="end_date"
                    type="datetime-local"
                    value={eventForm.end_date}
                    onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="assigned_to">Asignado a</Label>
                <Select
                  value={eventForm.assigned_to_user_id || "none"}
                  onValueChange={(value) => setEventForm({ ...eventForm, assigned_to_user_id: value === "none" ? '' : value })}
                >
                  <SelectTrigger id="assigned_to">
                    <SelectValue placeholder="Seleccionar usuario..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <ClientSearchSelect
                label="Cliente (opcional)"
                value={eventForm.customer_id}
                onValueChange={(value) => setEventForm({ ...eventForm, customer_id: value })}
              />

              <TicketSearchSelect
                label="Ticket (opcional)"
                value={eventForm.ticket_id}
                onValueChange={(value) => setEventForm({ ...eventForm, ticket_id: value })}
              />

              <div>
                <Label htmlFor="status">Estado *</Label>
                <Select
                  value={eventForm.status}
                  onValueChange={(value) => setEventForm({ ...eventForm, status: value })}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusConfig).map(([status, config]) => (
                      <SelectItem key={status} value={status}>
                        <Badge className={config.color}>{config.label}</Badge>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  placeholder="Detalles adicionales del evento..."
                  rows={4}
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                {editingEvent && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDeleteEvent}
                  >
                    Eliminar
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEventDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-[#9C0F17] hover:bg-[#7A0C12]"
                >
                  {editingEvent ? 'Actualizar' : 'Crear'} Evento
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
